import type { ID, ISODateString } from '@/types/common';

export interface NotificationSender {
  _id: ID;
  email: string;
  username: string;
}

export interface NotificationRecipient {
  user: ID;
  isRead: boolean;
  _id: ID;
}

export interface ApiNotification {
  _id: ID;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  sender: NotificationSender;
  recipients?: NotificationRecipient[];
  isRead: boolean;
  createdAt: ISODateString;
  updatedAt?: ISODateString;
}

export interface NotificationPagination {
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

export interface NotificationsResponse {
  status: boolean;
  message: string;
  data: {
    notifications: ApiNotification[];
    pagination: NotificationPagination;
  };
  timestamp: string;
}

export interface SendNotificationRequest {
  title: string;
  message: string;
  recipientIds?: string;
  userIds?: string[];
  roles?: string[];
}

export interface SendNotificationResponse {
  status: boolean;
  message: string;
  data: any;
  timestamp: string;
}

// Helper function to format time
export const formatNotificationTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds} secs ago`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
};

// Helper function to get notification icon type
export const getNotificationIconType = (
  type: string
): 'order' | 'payment' | 'message' | 'alert' => {
  switch (type) {
    case 'info':
      return 'message';
    case 'warning':
      return 'alert';
    case 'success':
      return 'payment';
    case 'error':
      return 'alert';
    default:
      return 'message';
  }
};

// Helper function to group notifications by day
export const groupNotificationsByDay = (
  notifications: ApiNotification[]
): Record<string, ApiNotification[]> => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const groups: Record<string, ApiNotification[]> = {};

  notifications.forEach(notification => {
    const date = new Date(notification.createdAt);
    let label = 'Older';

    if (date.toDateString() === today.toDateString()) {
      label = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      label = 'Yesterday';
    }

    if (!groups[label]) {
      groups[label] = [];
    }

    groups[label].push(notification);
  });

  return groups;
};
